import { FormCategory, FormTemplate } from '@nrwl/rx-models';
import {
  RxListCompositeComponent,
  RxReactiveFormCompositeComponent,
  RxTextCompositeComponent,
} from '@nrwl/rx-forms';

import { DialogsConfigService } from '../dialogs-config.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Validators } from '@angular/forms';
import { rxRequiredValidator } from '@nrwl/rx-validators';

@Injectable({
  providedIn: 'root',
})
export class EditorDialogConfigService extends DialogsConfigService {
  constructor() {
    super();
  }

  public confirmLeavingPage() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Confirm Navigation',
        tip: 'You have unsaved changes. Are you sure you want to leave this page?',
        buttons: this.SubmitDismissButtons('Leave this Page', 'Stay on this Page'),
      },
    };
  }

  public confirmDiscardChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Discard Changes',
        tip: 'You have unsaved changes. Are you sure you want to leave this page?',
        buttons: this.SubmitDismissButtons('Discard', 'Cancel'),
      },
    };
  }

  public confirmDiscardChangesAndStay() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Discard Changes',
        tip: 'Are you sure you want to discard unsaved changes?',
        buttons: this.SubmitDismissButtons('Discard', 'Cancel'),
      },
    };
  }

  public options() {
    return {
      component: RxTextCompositeComponent,
      panelClass: 'editor-options',
      width: null,
      payload: {
        forceValidate: true,
        disableScrollbars: false,
        title: 'Properties',
        tip: 'TODO: Explanatory note',
        buttons: this.SubmitDismissButtons('Apply', 'Cancel'),
        widget: {},
      },
    };
  }

  public customHeaderAndFooterOptions() {
    return {
      panelClass: 'editor-options',
      width: '700px',
      payload: {
        disableScrollbars: false,
        title: 'Header/Footer',
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
        data: null,
      },
    };
  }

  public conditions() {
    return {
      component: RxTextCompositeComponent,
      panelClass: 'editor-options',
      width: null,
      payload: {
        disableScrollbars: false,
        title: 'Conditions',
        tip: 'TODO: Explanatory note',
        buttons: this.SubmitDismissButtons('Apply', 'Cancel'),
        widget: {},
        formData: {},
      },
    };
  }

  public preview() {
    return {
      component: RxTextCompositeComponent,
      panelClass: 'ipadPreview',
      item: null,
      payload: {
        title: 'Form Preview',
        tip: 'TODO: Explanatory note',
        data: {},
        formTemplateId: null,
        suppress403: false,
        buttons: this.DismissButton('Close'),
      },
    };
  }

  public phonePreview() {
    return {
      component: RxTextCompositeComponent,
      panelClass: 'phone-preview',
      width: '327px',
      payload: {
        data: {},
      },
    };
  }

  public editSection(sectionName: string) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Rename section',
        fields: [
          {
            placeholder: 'Section name',
            labelText: null,
            hasInlineInfo: true,
            value: sectionName,
            maxLength: 100,
            validators: [Validators.maxLength(100)],
          },
        ],
        buttons: this.SubmitDismissButtons(),
      },
    };
  }

  public confirmDeleteSection() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Delete Section',
        tip: "Are you sure you want to delete this section and remove all it's contents?",
        buttons: this.SubmitDismissButtons('Delete', 'Cancel'),
      },
    };
  }

  public versionError() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Incompatible version',
        tip: '' + 'Selected form version is incompatible with Form Editor.',
        tipClose: false,
        buttons: this.SubmitButton('I understand'),
      },
    };
  }

  public fbImageError() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Alert',
        tip:
          '' +
          'The selected form has a corrupted image associated affecting displaying the form. Please contact support@rxphoto.com.',
        tipClose: false,
        buttons: this.SubmitButton('Ok'),
      },
    };
  }

  public formTitleEmptyError() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Form name',
        tip: '' + 'Selected form version is incompatible with Form Editor.',
        tipClose: false,
        buttons: this.SubmitButton('I understand'),
      },
    };
  }

  public NewForm() {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'New Template',
        placeholder: 'Form title',
        tip: 'All forms are catalogued by title, providers can filter forms by these titles.',
        tipClose: false,
        buttons: this.SubmitDismissButtons(),
        fields: [
          {
            placeholder: 'Form title',
            labelText: null,
            hasInlineInfo: true,
            value: '',
            maxLength: 100, // DO NOT REMOVE, Used in form-editor and form-container
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(100)],
            // asyncValidators: [this.formsValidatorsService.formTemplateUniqValidator(SearchType.AccountLibrary)]
          },
        ],
      },
    };
  }

  public NoWidgets() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'No Widgets',
        tip: '' + 'Please add at least one widget to save the form.',
        tipClose: false,
        buttons: this.SubmitButton('I understand'),
      },
    };
  }

  public SelectFormCategory(items$: Observable<FormCategory[]>, loading$?: Observable<boolean>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Select Folder',
        items$: items$,
        loading$: loading$,
        buttons: this.SubmitDismissButtons('Select', 'Cancel'),
      },
    };
  }

  public SaveAsBrowser(items$: Observable<FormCategory[]>) {
    return {
      component: RxListCompositeComponent,
      payload: {
        title: 'Destination',
        items$: items$,
        buttons: this.SubmitDismissButtons('Select'),
      },
    };
  }

  public SaveAsConfirmation(formCategory: FormCategory, formTemplates: FormTemplate[]) {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Save as Copy',
        tip: `Save ${formTemplates.length} form(s) to '${formCategory.name}'?`,
        buttons: this.SubmitDismissButtons('Yes'),
      },
    };
  }

  public TemplateExists() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Save as New',
        tip: `This Folder contains Template with the same name. Are you sure you want to save a duplicate?`,
        buttons: this.SubmitDismissButtons('Save', 'Cancel'),
      },
    };
  }

  public SaveConfirmation() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Changes were made',
        tip: `You are about to overwrite the existing form. Are you sure you want to make changes?`,
        buttons: this.SubmitDismissButtons('Confirm', 'Cancel'),
      },
    };
  }

  public PleaseSaveChanges() {
    return {
      component: RxTextCompositeComponent,
      payload: {
        title: 'Changes were made',
        tip: `Please save changes and try again.`,
        buttons: this.DismissButton('Ok'),
      },
    };
  }

  public SaveAs(newTitle: string) {
    return {
      component: RxReactiveFormCompositeComponent,
      payload: {
        title: 'Save As ...',
        placeholder: 'Form title',
        tip: 'All forms templates are catalogues by title, users can filter forms by their titles.',
        tipClose: false,
        buttons: this.SubmitDismissButtons(),
        fields: [
          {
            placeholder: 'Form title',
            labelText: null,
            hasInlineInfo: true,
            value: newTitle,
            maxLength: 100, // DO NOT REMOVE, Used in form-editor and form-container
            validators: [rxRequiredValidator, Validators.minLength(2), Validators.maxLength(100)],
            // asyncValidators: [this.formsValidatorsService.formTemplateUniqValidator(SearchType.AccountLibrary)]
          },
        ],
      },
    };
  }
}
